import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Wand2 } from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { DECAL_SHAPES, DECAL_FONTS, DECAL_COLORS } from "@/data/catalog";
import { composeDecalImage } from "@/lib/buildImage";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

const Label = ({ children }) => (
  <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">{children}</p>
);

const Decals = () => {
  const [shapeId, setShapeId] = useState("windshield");
  const [text, setText] = useState("G5 AUTO");
  const [fontId, setFontId] = useState("speed");
  const [colorHex, setColorHex] = useState("#FF2A3B");
  const [width, setWidth] = useState(36);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [buildImage, setBuildImage] = useState(null);

  const openQuote = () => {
    setQuoteOpen(true);
    setBuildImage(null);
    composeDecalImage({
      shapeName: shape.name,
      ratio: shape.ratio,
      text: text || "YOUR TEXT",
      fontId,
      colorHex,
      widthIn: width,
      heightIn: height,
    })
      .then((c) => setBuildImage(c.toDataURL("image/jpeg", 0.85)))
      .catch(() => {});
  };

  const shape = DECAL_SHAPES.find((s) => s.id === shapeId);
  const font = DECAL_FONTS.find((f) => f.id === fontId);
  const colorName = DECAL_COLORS.find((c) => c.hex === colorHex)?.name || colorHex;
  const height = Math.max(3, Math.round(width / shape.ratio));

  const price = useMemo(() => shape.base + width * 1.4, [shape, width]);

  const preset = {
    source: "decal_studio",
    service: "Custom Decals",
    custom_decal_specs: {
      shape: shape.name,
      text,
      style: font.name,
      size_inches: width,
      color: colorName,
      estimated_price: Math.round(price),
    },
    buildImage,
    summary: `DECAL — ${shape.name}\nTEXT — "${text}"\nFONT — ${font.name}\nCOLOR — ${colorName}\nSIZE — ${width}" W × ${height}" H\nESTIMATE — ${money(price)}`,
  };

  return (
    <main data-testid="decals-view" className="pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="font-mono text-xs tracking-[0.25em] text-velocity uppercase font-bold mb-3">
            Vinyl Lettering Lab
          </p>
          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-6xl leading-none">
            Custom <span className="text-velocity">Decals.</span>
          </h1>
          <p className="mt-4 max-w-xl text-slate-400 leading-relaxed">
            Windshield banners, door numbers, rocker scripts and tailgate badges — cut from high-tack outdoor cast
            vinyl. Type it, size it, see it.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* PREVIEW BOARD */}
          <div className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="rounded-2xl border border-edge bg-gunmetal blueprint-grid overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-edge/70">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">
                    Mockup Board · Window Glass
                  </span>
                  <span data-testid="decal-dimension-label" className="font-mono text-[10px] tracking-[0.25em] text-velocity uppercase">
                    {width}" × {height}"
                  </span>
                </div>

                <div className="relative p-4 sm:p-12 min-h-[300px] sm:min-h-[380px] flex items-center justify-center">
                  <div className="absolute inset-6 rounded-xl bg-gradient-to-br from-[#1A212C] via-[#0D1117] to-[#05070A] border border-edge/70 shadow-inner" />
                  <div className="absolute inset-6 rounded-xl overflow-hidden">
                    <div className="absolute -top-10 left-1/4 h-40 w-1/3 bg-white/5 blur-2xl rotate-12" />
                  </div>

                  <div className="relative w-full max-w-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-px flex-1 bg-velocity/60" />
                      <span className="font-mono text-[10px] text-velocity">{width}"</span>
                      <div className="h-px flex-1 bg-velocity/60" />
                    </div>
                    <div className="relative flex">
                      <div className="flex flex-col items-center justify-center mr-2">
                        <div className="w-px flex-1 bg-velocity/60" />
                        <span className="font-mono text-[10px] text-velocity -rotate-90 my-2">{height}"</span>
                        <div className="w-px flex-1 bg-velocity/60" />
                      </div>
                      <div
                        data-testid="decal-preview-board"
                        className="flex-1 border border-dashed border-steel/50 rounded-md flex items-center justify-center overflow-hidden py-6 px-2"
                        style={{ aspectRatio: `${shape.ratio} / 1` }}
                      >
                        <span
                          data-testid="decal-preview-text"
                          className={`${font.className} leading-none whitespace-nowrap select-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]`}
                          style={{
                            color: colorHex,
                            fontSize: `clamp(1.2rem, ${Math.min(8, 26 / Math.max(1, text.length) * shape.ratio)}vw + 1rem, 4.5rem)`,
                            textShadow: colorHex === "#FFFFFF" || colorHex === "#CBD5E1" ? "0 0 14px rgba(255,255,255,0.25)" : `0 0 18px ${colorHex}55`,
                          }}
                        >
                          {text || "YOUR TEXT"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-velocity/40 bg-panel p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 shadow-[0_0_30px_rgba(255,42,59,0.12)]">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-steel uppercase">Unit Estimate</p>
                  <p data-testid="decal-price-estimate" className="font-display font-black text-2xl sm:text-3xl text-white">
                    {money(price)}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">Includes weeding, transfer tape & install kit.</p>
                </div>
                <button
                  data-testid="decal-submit-quote-btn"
                  onClick={openQuote}
                  className="inline-flex items-center gap-2 bg-velocity hover:bg-velocitydark text-white text-xs sm:text-sm font-semibold px-4 py-2.5 sm:px-6 sm:py-3 rounded-full transition-all duration-200 shadow-[0_0_24px_rgba(255,42,59,0.4)] hover:-translate-y-0.5"
                >
                  <Wand2 className="h-4 w-4" /> Request This Decal
                </button>
              </div>
            </div>
          </div>

          {/* CONTROLS */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <section>
              <Label>Decal Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {DECAL_SHAPES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`decal-shape-select-${s.id}`}
                    onClick={() => setShapeId(s.id)}
                    className={`text-left rounded-xl border p-4 transition-all duration-200 ${
                      shapeId === s.id ? "border-velocity bg-velocity/10" : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <p className="font-display font-bold text-sm tracking-tight leading-tight">{s.name}</p>
                    <p className="font-mono text-[10px] text-velocity mt-2">FROM {money(s.base)}</p>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <Label>Your Text</Label>
              <Input
                data-testid="decal-text-input"
                value={text}
                maxLength={18}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your lettering"
                className="bg-panel border-edge"
              />
            </section>

            <section>
              <Label>Typeface</Label>
              <div className="space-y-2">
                {DECAL_FONTS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`decal-font-select-${f.id}`}
                    onClick={() => setFontId(f.id)}
                    className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition-all duration-200 ${
                      fontId === f.id ? "border-velocity bg-velocity/10" : "border-edge bg-panel hover:border-steel"
                    }`}
                  >
                    <span className={`${f.className} text-base text-slate-200`}>{f.name}</span>
                    {fontId === f.id && <Check className="h-4 w-4 text-velocity" />}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <Label>Vinyl Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {DECAL_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    data-testid={`decal-color-swatch-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setColorHex(c.hex)}
                    title={c.name}
                    aria-label={c.name}
                    className={`aspect-square rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                      colorHex === c.hex ? "border-velocity shadow-[0_0_14px_rgba(255,42,59,0.4)]" : "border-edge"
                    }`}
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
              <p className="mt-3 font-mono text-xs text-slate-400 uppercase tracking-wider">{colorName}</p>
            </section>

            <section>
              <Label>Width — {width} inches</Label>
              <Slider
                data-testid="decal-size-slider"
                value={[width]}
                min={6}
                max={60}
                step={1}
                onValueChange={([v]) => setWidth(v)}
                className="py-2"
              />
              <div className="flex justify-between font-mono text-[10px] text-steel mt-1">
                <span>6"</span>
                <span>60"</span>
              </div>
            </section>
          </div>
        </div>
      </div>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} preset={preset} />
      <section data-testid="decal-studio-info" className="max-w-3xl mx-auto px-4 sm:px-8 mt-12 sm:mt-16 border-t border-edge pt-8 sm:pt-10">
        <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight mb-4">Custom Vinyl Decals, Lettering & Graphics in Springdale</h2>
        <div className="space-y-4 text-sm text-slate-400 leading-relaxed">
          <p>Need a windshield banner for your build, door numbers for track day, or clean logo lettering for a work truck? The G5 decal studio cuts your text from high-tack outdoor cast vinyl in your choice of typeface, size and color — from reflective silver and signature red to metallic gold and neon lime. Preview it live on the mockup board with real dimensions before you order, so there are no surprises when it's time to install. Every decal ships weeded and masked with transfer tape and an install kit, or our crew can apply it in the shop for a perfect finish.</p>
          <p>We produce single decals for weekend cars and full lettering packages for commercial fleets across Northwest Arkansas. Because we work in cast vinyl rated for years of outdoor exposure, your graphics survive car washes, highway speeds and summer heat without peeling or cracking. Common orders include windshield sunstrips, rocker panel scripts, tailgate badges, business hours on glass, and matching sets for race teams. Send your design through the quote form and we'll confirm sizing, material and pricing within one business day.</p>
        </div>
      </section>

    </main>
  );
};

export default Decals;
